import {
  LeaseChargeTypeIsRecurring,
  RelationshipType,
  ScheduledChargeGroup,
  ScheduledChargeSource,
  TriggerIdNotForSecondaryApplicant,
} from '@common/entities';

export function isCoapplicantOrGuarantor(relationshipType: RelationshipType | undefined): boolean {
  return (
    relationshipType === RelationshipType.Coapplicant ||
    relationshipType === RelationshipType.Guarantor
  );
}

export function isBillingChargeForApplicant(
  charge: ScheduledChargeGroup,
  applicantCustomerId: number | undefined,
  relationshipType: RelationshipType | undefined,
): boolean {
  if (charge.source !== ScheduledChargeSource.Billing) return false;
  const triggerExcludedForSecondary =
    isCoapplicantOrGuarantor(relationshipType) &&
    TriggerIdNotForSecondaryApplicant.has(charge.arTriggerId);

  if (LeaseChargeTypeIsRecurring.has(charge.arTriggerId)) return true;

  return (
    (charge.customerId === null || charge.customerId === applicantCustomerId) &&
    !triggerExcludedForSecondary
  );
}

function isOptionalPricingCharge(charge: ScheduledChargeGroup): boolean {
  const requirementType = charge.allChargesDetails?.[0]?.requirementType;

  if (requirementType) {
    return requirementType.toLowerCase() === 'optional';
  }

  // Preserve compatibility with Pricing charge objects that do not include
  // allChargesDetails.
  return charge.isOptional === true;
}

/**
 * Pricing rows are custom fees from the Pricing API. Optional rows are shown
 * only when the property's DISPLAY_OPTIONAL_CUSTOM_FEES setting is enabled.
 */
export function isPricingChargeForRelationship(
  charge: ScheduledChargeGroup,
  relationshipType: RelationshipType | undefined,
  displayOptionalCustomFees = false,
): boolean {
  if (charge.source !== ScheduledChargeSource.Pricing) return false;

  // Property pricing charges have no optional flag or custom-fee metadata.
  if (!charge.isOptional && !charge.allChargesDetails?.length) return false;

  // Required custom fees are visible; optional custom fees follow the property setting.
  return !isOptionalPricingCharge(charge) || displayOptionalCustomFees;
}

/** Billing rows first (then pricing), preserving API order within each group. */
export function filterScheduledChargesForApplicant(
  charges: ScheduledChargeGroup[],
  applicantCustomerId: number | undefined,
  relationshipType: RelationshipType | undefined,
  displayOptionalCustomFees = false,
): ScheduledChargeGroup[] {
  const billing = charges.filter((c) =>
    isBillingChargeForApplicant(c, applicantCustomerId, relationshipType),
  );
  const pricing = charges.filter((c) =>
    isPricingChargeForRelationship(c, relationshipType, displayOptionalCustomFees),
  );
  return [...billing, ...pricing];
}
